import {
    AiGeneratedLabel,
    Button,
    Skeleton,
    Text,
} from '@/ui/design-system/components';
import {
    AlertCircle,
    Check,
    LoaderCircle,
    SlidersVertical,
    X,
} from 'lucide-react';
import { cn } from '@/ui/design-system/utils/cn';

const TYPE_BADGE_CLASSES = {
    brand: 'border border-[#E7D9FF] bg-[#F3ECFF] text-[#7A42F4]',
    category: 'border border-[#D8EBFF] bg-[#EAF5FF] text-[#1C78F2]',
    topic: 'border border-[#D8EBFF] bg-[#EAF5FF] text-[#1C78F2]',
};

function normalizeSnippet(text) {
    return (text || '').replace(/\s+/g, ' ').trim();
}

function getGeneratedSnippet(text) {
    if (!text) {
        return '';
    }

    const withoutHeadings = text
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line && !line.startsWith('#'))
        .join(' ');

    return normalizeSnippet(withoutHeadings || text);
}

export function isContextGenerating(context) {
    return context?.territory_status === 'generating';
}

export function isContextPending(context) {
    return context?.territory_status === 'pending';
}

export function getContextTypeLabel(contextType) {
    return contextType === 'brand' ? 'Brand' : 'Topic';
}

export function ContextTypeBadge({ contextType, className }) {
    return (
        <span
            className={cn(
                'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium leading-4',
                TYPE_BADGE_CLASSES[contextType] ||
                    'border border-slate-200 bg-slate-100 text-slate-700',
                className,
            )}
        >
            {getContextTypeLabel(contextType)}
        </span>
    );
}

// Low-em, not bg-muted: the AI label's default gray is under 4.5:1 on bg-muted.
const SNIPPET_BOX_CLASS =
    'rounded-[10px] border border-med-em bg-background-low-em px-2.5 py-2.5';

function ContextStatusBox({ status, className }) {
    const isGenerating = status === 'generating';
    return (
        <div className={cn(SNIPPET_BOX_CLASS, className)}>
            <div className="flex items-center justify-between gap-3 text-[#6B6B6B]">
                <span className="flex items-center gap-2 text-[14px] font-medium leading-6">
                    {isGenerating ? (
                        <LoaderCircle className="h-4 w-4 animate-spin text-[#A3A3A3]" />
                    ) : (
                        <AlertCircle className="h-4 w-4 text-[#A3A3A3]" />
                    )}
                    {isGenerating
                        ? 'AI is generating context...'
                        : 'Context needs regeneration.'}
                </span>
            </div>
        </div>
    );
}

function ContextSourceBox({ context }) {
    if (context.context_type === 'brand' && context.website_url) {
        return (
            <div className="rounded-[10px] border border-[#E6E7EB] bg-white px-2.5 py-2.5">
                <div className="flex items-center gap-2 text-[14px] leading-5 text-[#1A1A1A]">
                    <span className="text-[#808080]">URL:</span>
                    <span className="truncate font-medium">
                        {context.website_url}
                    </span>
                </div>
            </div>
        );
    }

    if (context.instructions) {
        return (
            <div className="rounded-[10px] border border-[#E6E7EB] bg-white px-2.5 py-2.5">
                <div className="flex items-center gap-2 text-[14px] leading-5 text-[#1A1A1A]">
                    <span className="text-[#808080]">Instructions:</span>
                    <span className="truncate font-medium">
                        {normalizeSnippet(context.instructions)}
                    </span>
                </div>
            </div>
        );
    }

    return null;
}

function ContextSnippetBox({ text, aiGenerated = true }) {
    return (
        <div className={SNIPPET_BOX_CLASS}>
            {aiGenerated ? <AiGeneratedLabel className="mb-1 flex" /> : null}
            <Text
                as="p"
                size="sm"
                className="text-[14px] leading-5 line-clamp-2"
            >
                {text}
            </Text>
        </div>
    );
}

export function AlertSelectedContextCard({ context, onRemove }) {
    const generating = isContextGenerating(context);
    const pending = isContextPending(context);
    const generatedSnippet = getGeneratedSnippet(context.territory_text);
    const fallbackSnippet =
        normalizeSnippet(context.instructions) ||
        normalizeSnippet(context.website_url);

    return (
        <div className="rounded-[10px] border border-[#D9D9D9] bg-white p-2.5 font-sans">
            <div className="flex items-center justify-between gap-3">
                <Text
                    as="p"
                    className="min-w-0 flex-1 line-clamp-1 text-sm font-medium leading-normal text-[#1A1A1A] font-sans"
                >
                    {context.name}
                </Text>
                <div className="flex shrink-0 items-center gap-2">
                    <ContextTypeBadge contextType={context.context_type} />
                    <button
                        type="button"
                        className="inline-flex h-8 w-8 items-center justify-center text-[#5A5A5A] transition-colors hover:text-[#1A1A1A]"
                        onClick={() => onRemove?.(context.alert_context_id)}
                        aria-label={`Remove ${context.name}`}
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
            </div>
            <div className="mt-2.5">
                {generating || pending ? (
                    <ContextStatusBox
                        status={generating ? 'generating' : 'pending'}
                    />
                ) : (
                    <ContextSnippetBox
                        text={
                            generatedSnippet ||
                            fallbackSnippet ||
                            'No generated content yet.'
                        }
                        aiGenerated={Boolean(generatedSnippet)}
                    />
                )}
            </div>
        </div>
    );
}

export function ContextManagerCard({
    context,
    isActive,
    isSelected,
    onActivate,
    onToggleSelected,
    onEdit,
    blockedByLimit = false,
}) {
    const generating = isContextGenerating(context);
    const pending = isContextPending(context);
    const generatedSnippet = getGeneratedSnippet(context.territory_text);

    return (
        <div
            className={cn(
                'rounded-[10px] border p-2.5 transition-colors cursor-pointer font-sans',
                isActive
                    ? 'border-[#8B5CF6] bg-[#F5F1FF]'
                    : 'border-[#D9D9D9] bg-white',
            )}
            onClick={onActivate}
        >
            <div className="flex items-start justify-between gap-3">
                <Text
                    as="p"
                    className="line-clamp-1 text-sm font-medium leading-normal text-[#1A1A1A] font-sans"
                >
                    {context.name}
                </Text>
                <ContextTypeBadge contextType={context.context_type} />
            </div>

            <div className="mt-3 flex flex-col gap-2">
                {generating || (pending && !context.territory_text) ? (
                    <Text
                        as="p"
                        className="text-[14px] leading-5 text-[#6B6B6B]"
                    >
                        {generating
                            ? 'AI is generating context...'
                            : 'Context needs regeneration.'}
                    </Text>
                ) : (
                    <>
                        <ContextSourceBox context={context} />
                        {generatedSnippet ? (
                            <ContextSnippetBox text={generatedSnippet} />
                        ) : null}
                    </>
                )}
            </div>

            <div className="mt-4 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className={cn(
                        'h-10 w-full justify-start rounded-md px-4 text-[14px] whitespace-nowrap xl:w-auto',
                        isSelected
                            ? 'border-[#E5E7EB] bg-[#F5F5F5] text-[#111827] hover:bg-[#F5F5F5]'
                            : 'border-[#D9D9D9] bg-white hover:bg-[#F8F8F8]',
                    )}
                    disabled={!isSelected && blockedByLimit}
                    onClick={(event) => {
                        event.stopPropagation();
                        onToggleSelected?.();
                    }}
                >
                    {isSelected ? <Check className="h-4 w-4" /> : null}
                    {isSelected ? 'Selected' : 'Select context'}
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-10 w-full justify-start rounded-md px-4 text-[14px] whitespace-nowrap xl:w-auto"
                    onClick={(event) => {
                        event.stopPropagation();
                        onEdit?.();
                    }}
                >
                    <SlidersVertical className="h-4 w-4" />
                    Edit context
                </Button>
            </div>
        </div>
    );
}

export function ContextOverviewSkeleton() {
    return (
        <div className="flex flex-col gap-4 pt-5">
            <Skeleton className="h-4 w-[160px] rounded-full bg-[#E5E7EB]" />
            <Skeleton className="h-4 w-full rounded-full bg-[#E5E7EB]" />
            <Skeleton className="h-4 w-full rounded-full bg-[#E5E7EB]" />
            <Skeleton className="h-4 w-full rounded-full bg-[#E5E7EB]" />
            <Skeleton className="h-4 w-full rounded-full bg-[#E5E7EB]" />
            <Skeleton className="h-4 w-full rounded-full bg-[#E5E7EB]" />
            <Skeleton className="h-4 w-[240px] rounded-full bg-[#E5E7EB]" />
        </div>
    );
}
